import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { subscribeToPush, isPushSupported } from '../../lib/pushNotifications';

/**
 * Global Chat Notifications component
 * Runs globally in App.jsx.
 * Displays OS-level desktop notifications on Windows/macOS/Linux
 * even if the user is working in another application outside of Chrome.
 */
export default function GlobalChatNotifications() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userMembershipsRef = useRef(new Set());
  const seenMessageIdsRef = useRef(new Set());

  // 1. Ensure Push subscription is active if permission was previously granted
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !isPushSupported()) return;

    if (
      Notification.permission === 'granted' ||
      localStorage.getItem('app_desktop_notifications') === 'true'
    ) {
      subscribeToPush(user.id).catch((err) => {
        console.warn('Silent push subscription ensure warning:', err);
      });
    }
  }, [user?.id, isAuthenticated]);

  // 2. Load and cache user's active conversation memberships
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      userMembershipsRef.current = new Set();
      return undefined;
    }

    const loadMemberships = async () => {
      try {
        const { data } = await supabase
          .from('conversation_members')
          .select('conversation_id')
          .eq('user_id', user.id);

        if (data) {
          userMembershipsRef.current = new Set(data.map((m) => m.conversation_id));
        }
      } catch (err) {
        console.warn('Unable to load user conversation memberships:', err);
      }
    };

    loadMemberships();

    // Listen for new conversations joined
    const memberChannel = supabase
      .channel(`global-member-changes:${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversation_members', filter: `user_id=eq.${user.id}` },
        loadMemberships
      )
      .subscribe();

    return () => {
      supabase.removeChannel(memberChannel);
    };
  }, [user?.id, isAuthenticated]);

  // 3. Listen to incoming messages globally and show Windows OS Toast notifications
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;

    const showNotification = async (message) => {
      const senderName = message.sender_name || 'Mesazh i ri';
      const notificationTitle = senderName;
      const notificationBody = `${senderName} të ka shkruar një mesazh.`;
      const tag = `chat-${message.conversation_id}`;
      const url = `/chat`;

      // Check if browser/OS supports service worker notifications (displays in Windows Action Center)
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          if (registration && registration.showNotification) {
            await registration.showNotification(notificationTitle, {
              body: notificationBody,
              icon: `${process.env.PUBLIC_URL || ''}/icon-192.png`,
              badge: `${process.env.PUBLIC_URL || ''}/favicon.png`,
              tag,
              renotify: true,
              data: {
                url,
                conversationId: message.conversation_id,
              },
            });
            return;
          }
        } catch (swErr) {
          console.warn('Service worker showNotification failed, trying fallback:', swErr);
        }
      }

      // Standard desktop Notification fallback
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const n = new Notification(notificationTitle, {
            body: notificationBody,
            icon: `${process.env.PUBLIC_URL || ''}/icon-192.png`,
            tag,
          });
          n.onclick = () => {
            window.focus();
            navigate('/chat', { state: { chatConversationId: message.conversation_id } });
            n.close();
          };
        } catch (nErr) {
          console.error('Desktop notification fallback error:', nErr);
        }
      }
    };

    const channel = supabase
      .channel(`global-chat-notifications:${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const message = payload.new;
        if (!message || message.sender_id === user.id) return;

        // Verify that the signed-in user is a member of this conversation
        if (
          userMembershipsRef.current.size > 0 &&
          !userMembershipsRef.current.has(message.conversation_id)
        ) {
          return;
        }

        // Avoid duplicate triggers
        if (seenMessageIdsRef.current.has(message.id)) return;
        seenMessageIdsRef.current.add(message.id);
        if (seenMessageIdsRef.current.size > 200) {
          seenMessageIdsRef.current = new Set([...seenMessageIdsRef.current].slice(-100));
        }

        // Check if user is actively in /chat looking at this exact conversation and window has focus
        const isCurrentChatActive =
          location.pathname === '/chat' &&
          document.hasFocus?.() &&
          document.visibilityState === 'visible' &&
          window.__CURRENT_ACTIVE_CHAT_ID === message.conversation_id;

        // If user is actively reading it right now on screen, do not show OS toast
        if (isCurrentChatActive) {
          return;
        }

        // Otherwise (even if user is in Word, VS Code, another app or another tab outside Chrome)
        // trigger the OS notification!
        showNotification(message);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, isAuthenticated, location.pathname, navigate]);

  return null;
}
